import Check from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import FileCopy from '@mui/icons-material/FileCopy';
import WhatsApp from '@mui/icons-material/WhatsApp';
import { Box, Button, Grid, Typography } from '@mui/material';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import useWidth from '../../../components/Hooks/useWidth';
import config from '../../../config';
import useClipboard from '../../../hooks/useClipboard';
import useHandleApiError from '../../../hooks/useHandleApiError';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import useTeceoStyles from '../../../useTeceoStyles';
import { sleep } from '../../../utils/generics';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../translation/TranslationContext';
import { useOrderActionsContext } from '../contexts/OrderActions.context';
import { useSignatureContext } from './contexts/signature.context';
import { signedContractRepository } from './repository/signedContract.repository';
import { ModalContractPage } from './types';
import { getModalContentXPadding } from './utils';

const MethodOfSigning = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [link, setLink] = useState('');
  const { changePage, contractInfo, signingAs } = useSignatureContext();
  const { translate } = useTranslation();
  const { classes: teceoClasses } = useTeceoStyles();
  const { orderRaw } = useOrderActionsContext();
  const { smallWidth } = useWidth();
  const px = getModalContentXPadding(smallWidth);
  const clipboard = useClipboard();
  const { isBrand } = useApplicationContext();
  const { handleMessage } = snackbarStore;
  const { getApiErrorMessage } = useHandleApiError();

  const handleSendWhatsApp = (receivedLink: string) => {
    if (!receivedLink) {
      snackbarStore.handleMessage({
        message: translate(LangEnum.ORDER_CONTRACT_WHATSAPP_LINK_ERROR),
        type: 'error',
      });
      return;
    }

    window.open(
      `https://api.whatsapp.com/send?text=${translate(
        LangEnum.ORDER_CONTRACT_WHATSAPP_LINK_MESSAGE,
        { orderCode: orderRaw?.orderCode },
      )}: ${receivedLink}`,
      '_blank',
    );
  };

  const handleSendEmail = async () => {
    if (!contractInfo?.id || !orderRaw?.orderId) {
      return;
    }

    const response = await signedContractRepository().sendEmailToSignContract(
      contractInfo.id,
      orderRaw.orderId,
    );

    if (!response.ok) {
      return handleMessage({
        message: getApiErrorMessage(response),
        type: 'error',
      });
    }

    handleMessage({
      message: translate(LangEnum.ORDER_CONTRACT_SIGNATURE_EMAIL_SENT_SUCESSFULLY),
      type: 'success',
    });

    setIsEmailSent(true);
    await sleep(3000);
    setIsEmailSent(false);
  };

  const handleSignNow = () => {
    changePage(ModalContractPage.READ_AND_SIGN_CONTRACT);
  };

  const handleClickCopyLink = async () => {
    clipboard.copy(link);
    setIsCopied(true);
    await sleep(3000);
    setIsCopied(false);
  };

  useEffect(() => {
    const loadLink = async () => {
      if (!contractInfo || !signingAs) {
        return;
      }
      const response = await signedContractRepository().getSignExternalLink(
        contractInfo?.id,
        signingAs,
      );

      if (!response.data?.id) {
        return;
      }

      setLink(`${config.webUrl}/assinar-contrato/${response.data?.id}`);
    };

    loadLink();
  }, [signingAs, contractInfo]);

  return (
    <Box
      style={{ overflowX: 'hidden' }}
      sx={{
        height: '100%',
      }}
    >
      <Box
        sx={{
          p: 4,
          px: px,
        }}
      >
        <Box
          sx={{
            pb: smallWidth ? 2 : 4,
          }}
        >
          <Typography
            color="textSecondary"
            align="left"
            className={classnames(teceoClasses.secondaryTypography, teceoClasses.upperTitle)}
            style={{
              fontSize: '14px',
            }}
          >
            {translate(LangEnum.ORDER_CONTRACT_HOW_TO_SIGN)}
          </Typography>
        </Box>
        <Box
          sx={{
            pb: smallWidth ? 2 : 4,
          }}
        >
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography>
                {translate(LangEnum.ORDER_CONTRACT_SHARE_LINK_CALL_TO_ACTION)}:
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography
                id="link-to-copy"
                color="textSecondary"
                noWrap
                className={teceoClasses.truncate}
              >
                {link || translate(LangEnum.GLOBAL_LOADING)}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Grid container spacing={1}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      minWidth: '200px',
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={<WhatsApp />}
                      onClick={() => handleSendWhatsApp(link)}
                    >
                      <Typography>
                        {translate(LangEnum.ORDER_CONTRACT_SHARE_BY_WHATSAPP)}
                      </Typography>
                    </Button>
                  </Box>
                </Grid>
                {isBrand && signingAs === 'STORE' && (
                  <Grid
                    size={{
                      xs: 12,
                      sm: 'auto',
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={isEmailSent}
                      startIcon={<EmailIcon />}
                      onClick={handleSendEmail}
                    >
                      <Typography>{translate(LangEnum.ORDER_CONTRACT_SHARE_BY_EMAIL)}</Typography>
                    </Button>
                  </Grid>
                )}
                <Grid
                  size={{
                    xs: 12,
                    sm: 'auto',
                  }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={isCopied}
                    onClick={handleClickCopyLink}
                    startIcon={isCopied ? <Check /> : <FileCopy />}
                  >
                    <Typography>
                      {isCopied
                        ? translate(LangEnum.ORDER_CONTRACT_LINK_COPIED_TO_CLIPBOARD)
                        : translate(LangEnum.ORDER_CONTRACT_COPY_LINK)}
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            pt: 0.5,
          }}
        >
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography>
                {translate(
                  smallWidth
                    ? LangEnum.ORDER_CONTRACT_SIGN_NOW_CALL_TO_ACTION_MOBILE
                    : LangEnum.ORDER_CONTRACT_SIGN_NOW_CALL_TO_ACTION,
                )}
                .
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 'auto',
              }}
            >
              <Box
                sx={{
                  minWidth: '200px',
                }}
              >
                <Button fullWidth onClick={handleSignNow} color="primary" variant="outlined">
                  <Typography>{translate(LangEnum.ORDER_CONTRACT_SIGN_CALL_TO_ACTION)}</Typography>
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default MethodOfSigning;
